"use client"

import type React from "react"

import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
} from "@mui/material"
import { useMyLeanbotContext } from "./Layout"
import { useCallback, useEffect, useState, useRef } from "react"
import PagerTitle from "../../components/PagerTitle"
import { useAlert } from "../../contexts/AlertContext"

// Custom debounce implementation
const debounce = <F extends (...args: any) => any>(func: F, waitFor: number) => {
    let timeout: ReturnType<typeof setTimeout> | null = null

    const debounced = (...args: Parameters<F>) => {
        if (timeout !== null) {
            clearTimeout(timeout)
            timeout = null
        }
        timeout = setTimeout(() => func(...args), waitFor)
    }

    return debounced as (...args: Parameters<F>) => ReturnType<F>
}

export interface IMyLeanbotExtend {
    id: string
    leanbot_id: string
    partner_id: string
    created_at: string
    status: string
    image_url?: string
}

export default function MyLeanbot() {
    const [leanbots, setLeanbots] = useState<IMyLeanbotExtend[]>([])
    const [loading, setLoading] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [newLeanbotId, setNewLeanbotId] = useState("")
    const [isValidating, setIsValidating] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isValidLeanbotId, setIsValidLeanbotId] = useState(false)
    const { getDataMyLeanbot, validateLeanbotId, registerLeanbot, uploadAvatarLeanbot } = useMyLeanbotContext()
    const alert = useAlert()

    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Use a ref to track the latest validation result
    const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const handleOpenDialog = () => {
        setDialogOpen(true)
        setNewLeanbotId("")
        setIsValidLeanbotId(false)
    }

    const handleCloseDialog = () => {
        setDialogOpen(false)
        setNewLeanbotId("")
        setIsValidLeanbotId(false)

        // Clear any pending validation
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current)
            validationTimeoutRef.current = null
        }
    }

    const handleLeanbotIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setNewLeanbotId(value)

        // Clear any pending validation
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current)
            validationTimeoutRef.current = null
        }

        // Reset validation state when input changes
        if (isValidLeanbotId) {
            setIsValidLeanbotId(false)
        }
    }

    const validateLeanbotIdInput = async (id: string) => {
        if (!id.trim()) return

        setIsValidating(true)
        try {
            // Call API to validate Leanbot ID
            const response = await validateLeanbotId(id)

            console.log("Validation response:", response) // Debug log

            if (response.code === 200 && response.valid === true) {
                setIsValidLeanbotId(true) // This should enable the button
                alert.success(response.message || "Valid Leanbot ID")
            } else {
                setIsValidLeanbotId(false)
                alert.error(response.message || "Invalid Leanbot ID")
            }
        } catch (err: any) {
            setIsValidLeanbotId(false)
            alert.error(err.message || "Error validating Leanbot ID")
        } finally {
            setIsValidating(false)
        }
    }

    const handleSubmitLeanbot = async () => {
        if (!newLeanbotId.trim() || isSubmitting) return

        setIsSubmitting(true)
        try {
            // First validate the Leanbot ID
            setIsValidating(true)
            const validationResult = await validateLeanbotId(newLeanbotId)
            setIsValidating(false)

            if (validationResult.code === 200 && validationResult.valid === true) {
                // If validation successful, register the Leanbot
                const result = await registerLeanbot(newLeanbotId)
                if (result.success) {
                    alert.success("Leanbot registered successfully!")
                    handleCloseDialog()
                    loadData() // Reload data to show the new leanbot
                } else {
                    alert.error(result.message || "Failed to register Leanbot. Please try again.")
                }
            } else {
                // If validation failed, show error
                alert.error(validationResult.message || "Invalid Leanbot ID")
            }
        } catch (error) {
            console.error("Error registering leanbot:", error)
            alert.error("An error occurred while registering the Leanbot. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    const loadData = useCallback(() => {
        setLoading(true)
        getDataMyLeanbot()
            .then((response) => {
                if (response.code === 200 && response.status === "success") {
                    setLeanbots(response.data || [])
                }
            })
            .finally(() => {
                setLoading(false)
            })
    }, [getDataMyLeanbot])

    useEffect(() => {
        loadData()

        // Cleanup function to clear any pending validation on unmount
        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current)
            }
        }
    }, [loadData])

    // For debugging - log state changes
    useEffect(() => {
        console.log("isValidLeanbotId changed:", isValidLeanbotId)
    }, [isValidLeanbotId])

    // Function to format the leanbot ID with hyphens (e.g., ABC-123)
    const formatLeanbotId = (id: string) => {
        if (!id) return ""
        return id
        // return `${id.slice(0, 3)}-${id.slice(3)}`.toUpperCase()
    }

    // Mock data for UI display since the API doesn't provide usage information
    const getUsageData = (id: string) => {
        // This is mock data - in a real app, you would get this from the API
        const mockData = {
            totalRuntime: id.length > 5 ? "15h 30m" : "12h 15m",
            averageSession: id.length > 5 ? "25m" : "40m",
        }
        return mockData
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            setSelectedFile(file)
            uploadLeanbotImage(file, e.target.dataset.leanbotId || "")
        }
    }

    const handleChangePhoto = (leanbotId: string) => {
        if (fileInputRef.current) {
            fileInputRef.current.dataset.leanbotId = leanbotId
            fileInputRef.current.click()
        }
    }

    const uploadLeanbotImage = async (file: File, leanbotId: string) => {
        if (!file || !leanbotId) return

        setUploading(true)
        try {
            // Use the context function to upload the image
            const result = await uploadAvatarLeanbot(leanbotId, file)

            if (result.success) {
                alert.success("Image uploaded successfully!")

                // If we have an image_url in the response, update the leanbot in the state
                if (result.image_url) {
                    setLeanbots((prevLeanbots) =>
                        prevLeanbots.map((leanbot) =>
                            leanbot.leanbot_id === leanbotId ? { ...leanbot, image_url: result.image_url } : leanbot,
                        ),
                    )
                } else {
                    // If no image_url in response, reload all data
                    loadData()
                }
            } else {
                alert.error(result.message || "Failed to upload image")
            }
        } catch (error) {
            console.error("Error uploading image:", error)
            alert.error("An error occurred while uploading the image")
        } finally {
            setUploading(false)
            setSelectedFile(null)
        }
    }

    return (
        <Box
            sx={{
                p: 3,
                bgcolor: "white",
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                flex: 1,
            }}
        >
            <PagerTitle title="Physical Leanbots" />

            {leanbots.map((leanbot) => {
                const usageData = getUsageData(leanbot.leanbot_id)
                return (
                    <Paper
                        key={leanbot.id}
                        elevation={1}
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            borderRadius: 1,
                            border: "1px solid #eee",
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: "#f5f5f5",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                mr: 2,
                                borderRadius: 1,
                                border: "1px solid #ddd",
                                position: "relative",
                                overflow: "hidden",
                            }}
                        >
                            {leanbot.image_url ? (
                                <img
                                    src={leanbot.image_url || "/placeholder.svg"}
                                    alt="Leanbot"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                            ) : null}
                            <Button
                                size="small"
                                sx={{
                                    fontSize: "10px",
                                    color: "#666",
                                    position: leanbot.image_url ? "absolute" : "static",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    bgcolor: leanbot.image_url ? "rgba(255,255,255,0.7)" : "transparent",
                                }}
                                onClick={() => handleChangePhoto(leanbot.leanbot_id)}
                                disabled={uploading}
                            >
                                {uploading ? <CircularProgress size={16} /> : "Change Photo"}
                            </Button>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography component="div" sx={{ fontSize: "14px", color: "#666", mb: 1 }}>
                                ID:
                                <Typography component="span" sx={{ ml: 1, color: "#333", fontWeight: "medium" }}>
                                    {formatLeanbotId(leanbot.leanbot_id)}
                                </Typography>
                            </Typography>

                            <Typography component="div" sx={{ fontSize: "14px", color: "#666", mb: 1 }}>
                                Status:
                                <Typography
                                    component="span"
                                    sx={{
                                        ml: 1,
                                        color: "#2ecc71",
                                        fontWeight: "medium",
                                    }}
                                >
                                    {leanbot.status === "1" ? "Registered & Active" : "Inactive"}
                                </Typography>
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: "#666",
                                    mt: 1,
                                    mb: 1,
                                    fontWeight: "medium",
                                }}
                            >
                                Usage
                            </Typography>

                            {/* <Typography component="div" sx={{ fontSize: "14px", color: "#666", mb: 1 }}>
                                Total Runtime:
                                <Typography component="span" sx={{ ml: 1, color: "#333" }}>
                                    {usageData.totalRuntime}
                                </Typography>
                            </Typography>

                            <Typography component="div" sx={{ fontSize: "14px", color: "#666", mb: 1 }}>
                                Average Session:
                                <Typography component="span" sx={{ ml: 1, color: "#333" }}>
                                    {usageData.averageSession}
                                </Typography>
                            </Typography> */}

                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    mt: 1,
                                    bgcolor: "#f39c12",
                                    "&:hover": { bgcolor: "#e67e22" },
                                    textTransform: "none",
                                    fontWeight: "medium",
                                }}
                                component="a"
                                href="https://support.pythaverse.space/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Get Support
                            </Button>
                        </Box>
                    </Paper>
                )
            })}

            {leanbots.length === 0 && !loading && (
                <Typography sx={{ textAlign: "center", my: 4, color: "#666" }}>
                    No leanbots found. Register your first leanbot.
                </Typography>
            )}

            {loading && <Typography sx={{ textAlign: "center", my: 4, color: "#666" }}>Loading leanbots...</Typography>}

            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                <Button
                    variant="contained"
                    sx={{
                        bgcolor: "#16a085",
                        "&:hover": { bgcolor: "#1abc9c" },
                        textTransform: "none",
                        fontWeight: "medium",
                    }}
                    onClick={handleOpenDialog}
                >
                    Register Another Leanbot
                </Button>
            </Box>

            {/* Hidden file input for image upload */}
            <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*" onChange={handleFileSelect} />

            {/* Registration Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                PaperProps={{
                    sx: {
                        width: "100%",
                        maxWidth: "600px",
                        borderRadius: "4px",
                        bgcolor: "#f0f8ff",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "24px",
                        pt: 3,
                    }}
                >
                    INPUT YOUR LEANBOT ID
                </DialogTitle>
                <DialogContent sx={{ px: 4, py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography sx={{ minWidth: "120px", fontSize: "16px" }}>Leanbot ID:</Typography>
                        <TextField
                            fullWidth
                            placeholder="your Leanbot ID"
                            value={newLeanbotId}
                            onChange={handleLeanbotIdChange}
                            variant="outlined"
                            disabled={isSubmitting}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    bgcolor: "white",
                                },
                            }}
                            InputProps={{
                                endAdornment: isValidating && <CircularProgress size={20} sx={{ color: "primary.main" }} />,
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleSubmitLeanbot}
                        disabled={!newLeanbotId.trim() || isSubmitting}
                        sx={{
                            bgcolor: "#ff3b30",
                            "&:hover": { bgcolor: "#d63530" },
                            color: "white",
                            borderRadius: "20px",
                            px: 4,
                            "&.Mui-disabled": {
                                bgcolor: "#ffcccb",
                                color: "rgba(255, 255, 255, 0.8)",
                            },
                        }}
                    >
                        {isSubmitting || isValidating ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Submit"}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCloseDialog}
                        disabled={isSubmitting}
                        sx={{
                            bgcolor: "#f5f5f5",
                            "&:hover": { bgcolor: "#e0e0e0" },
                            color: "#333",
                            borderRadius: "20px",
                            px: 4,
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Debug information - remove in production */}
            <Box sx={{ display: "none" }}>
                <Typography>Debug Info:</Typography>
                <Typography>isValidLeanbotId: {isValidLeanbotId ? "true" : "false"}</Typography>
                <Typography>isValidating: {isValidating ? "true" : "false"}</Typography>
                <Typography>newLeanbotId: {newLeanbotId}</Typography>
            </Box>
        </Box>
    )
}
